// /login/mfa — 6-digit TOTP challenge after a successful password submit.
//
// Visual + copy built from
//   design/UI : UX v1.0/for-engineering-auth/specs/screen-strings.md § 3.
// CodeInput auto-submits on all-six-entered. The state machine handles wrong
// codes (returns to mfa-challenge with an error) and success (advances to
// authenticated).
//
// Trust-device days: spec says render `tenant.trustDeviceMaxDays`. Phase 0
// has no tenant context yet — hardcoded to 30 with a TODO.

import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  CodeInput,
  LangToggle,
} from '@gc-coach/design-system';
import { AdminAuthShell, useAuth } from '../../auth/index.js';
import { useI18n } from '../../i18n/index.js';
import styles from './AuthScreens.module.css';

// TODO Phase 1: source from tenant.trustDeviceMaxDays once tenant context lands.
const TRUST_DEVICE_MAX_DAYS = 30;

export function MFAChallengeScreen() {
  const { t, tt, lang, setLang } = useI18n();
  const { status, error, submitMfaCode, startMfaRecovery, clearError, signOut } = useAuth();

  const [code, setCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);

  const submitting = status.kind === 'submitting-mfa';
  const email = status.kind === 'mfa-challenge' ? status.email : '';

  // Clear the entered code when a wrong-code error fires (state transitions
  // submitting-mfa → mfa-challenge with error.code === 'WRONG_CODE'). Without
  // this, the bad digits stay in the 6 cells and the user has to tab back
  // through each one before retrying. Detect the transition via a ref so we
  // reset exactly once per failure (not on every render that sees the error).
  const prevStatusKindRef = useRef(status.kind);
  useEffect(() => {
    if (
      prevStatusKindRef.current === 'submitting-mfa' &&
      status.kind === 'mfa-challenge' &&
      error?.code === 'WRONG_CODE'
    ) {
      setCode('');
    }
    prevStatusKindRef.current = status.kind;
  }, [status.kind, error]);

  const onComplete = (full: string) => {
    if (full.length === 6 && !submitting) {
      void submitMfaCode(full);
      // Note: trustDevice flag isn't wired to Cognito yet (no "remember
      // device" SDK call in Option A v0); will land when device-trust ships.
    }
  };

  // Body has {email} interpolated and bolded.
  const bodyTemplate = t('mfaChallenge.bodyTemplate');
  const bodyParts = bodyTemplate.split('{email}');

  return (
    <AdminAuthShell>
      <div className={styles.topBar}>
          <LangToggle value={lang} onChange={setLang} />
        </div>

        <div className={styles.formColumn}>
          <header className={styles.header}>
            <span className={styles.eyebrow}>{t('mfaChallenge.eyebrow')}</span>
            <h1 className={styles.title}>{t('mfaChallenge.headline')}</h1>
            <p className={styles.body}>
              {bodyParts[0]}
              <strong className={styles.bodyEmphasis}>{email}</strong>
              {bodyParts[1]}
            </p>
          </header>

          <Card>
            <div className={styles.form}>
              {error && (
                <Alert severity="danger" onDismiss={clearError}>
                  {error.message}
                </Alert>
              )}

              <div className={styles.codeInputCenter}>
                <CodeInput
                  value={code}
                  onChange={setCode}
                  onComplete={onComplete}
                  ariaLabel={t('mfaChallenge.codeLabel')}
                  disabled={submitting}
                  errorMessage={error?.code === 'WRONG_CODE' ? error.message : null}
                />
              </div>

              <div>
                <Checkbox
                  label={tt('mfaChallenge.trustDeviceTemplate', { days: TRUST_DEVICE_MAX_DAYS })}
                  checked={trustDevice}
                  onChange={(e) => setTrustDevice(e.currentTarget.checked)}
                  disabled={submitting}
                />
                <p className={styles.captionUnder}>
                  {tt('mfaChallenge.trustDevicePolicyTemplate', { days: TRUST_DEVICE_MAX_DAYS })}
                </p>
              </div>

              <Button
                kind="brand"
                fullWidth
                loading={submitting}
                disabled={code.length !== 6 || submitting}
                onClick={() => onComplete(code)}
              >
                {t('mfaChallenge.submitButton')}
              </Button>
            </div>

            <div className={styles.divider}>
              <span className={styles.dividerText}>{t('common.or')}</span>
            </div>

            <div className={styles.fallbackStack}>
              <button type="button" className={styles.fallbackLink} disabled>
                {t('mfaChallenge.fallbackEmail')}
              </button>
              <button type="button" className={styles.fallbackLink} disabled>
                {t('mfaChallenge.fallbackRecovery')}
              </button>
              {/* Self-service MFA recovery via the Custom Auth Flow:
                * emails a 6-digit code to the account's verified address,
                * verifying ownership before letting the user re-enroll a
                * new authenticator. Replaces the earlier admin-notification
                * stop-gap. */}
              <button
                type="button"
                className={styles.fallbackLink}
                disabled={submitting || !email}
                onClick={() => void startMfaRecovery()}
              >
                {t('mfaChallenge.fallbackLostDevice')}
              </button>
              <button
                type="button"
                className={styles.fallbackLink}
                onClick={() => void signOut()}
              >
                {t('mfaChallenge.fallbackHelp')}
              </button>
            </div>
          </Card>
        </div>
    </AdminAuthShell>
  );
}
